import { action, observable, computed, makeObservable, flow } from "mobx";
import * as _ from "lodash";
import { Utils } from "../../common/utils";
import { EAIClient } from "Services/EAIClient";
import {featureGatingStore} from "Stores/index";

import CONFIG from "Config";

export interface SimpleSchool {
    display_name: string
    school_id: string
}

export interface Integration {
    schoolId: string
    name: string
    settings: any
}

export interface ModalOptions {
    element: JSX.Element,
    accept: () => void,
    cancel?: () => void,
    showActions: boolean,
    acceptText?: string,
    cancelText?: string,
}

export class EAIError {
    type: string;
    message: string;
    original_exception: Error;
    display: "inline" | "global";
    repeats: number;

    constructor(error: Error, customMessage?: string){
        this.type = error.name || "Unknown Type";
        this.message = customMessage || _.get(error, "response.data.result.message") || "An unknown error occurred";
        this.original_exception = error;
        this.display = "global";
        this.repeats = 0;
    }
}

class AppStore {
    client: EAIClient = new EAIClient();

    @observable confirmModal: ModalOptions | null = null;
    @observable errors: EAIError[];
    @observable shouldDisplayErrors: boolean;
    @observable isChangingMasquerade = false;
    @observable callbackOnError?: () => void | undefined;

    @observable isLoadingSchools = false;
    @observable isLoadingIntegrations = false;
    @observable schoolList: SimpleSchool[] = [];
    @observable selectedSchool: SimpleSchool | undefined = undefined;
    @observable integrations: Integration[] = [];

    constructor() {
        makeObservable(this);
    }

    @computed get globalErrors(){
        return _.filter(this.errors, {display: "global"});
    }

    @computed get inlineErrors(){
        return _.filter(this.errors, {display: "inline"});
    }

    @computed get isMasquerading(){
        return Utils.getIsMasquerading();
    }

    @computed get peopleAdminUrl(){
        const peopleAdmin = this.integrations.find(obj => obj.name.toUpperCase() == "PEOPLEADMIN");

        if (peopleAdmin) {
            return peopleAdmin.settings.url;
        }

        return null;
    }


    @action
    showConfirmModal(opts: ModalOptions){
        this.confirmModal = opts;
    }

    @action
    hideConfirmModal(){
        this.confirmModal = null;
    }

    @action
    addError(error: Error, customMessage?: string, callbackOnError?: () => void ){
        this.addEAIError(new EAIError(error, customMessage), callbackOnError);
    }

    @action
    addEAIError(error: EAIError, callbackOnError?: () => void ){

        if(!this.errors) this.errors = [];
        // prevent duplicates
        const existingIdx = _.findIndex(this.errors, {message: error.message});
        if(existingIdx > -1){
            this.errors[existingIdx].repeats++;
            return;
        }
        // eslint-disable-next-line no-console
        console.log(error);
        this.errors.push(error);

        if (callbackOnError) {
            this.callbackOnError = callbackOnError;
        }
        this.shouldDisplayErrors = true;
    }

    @action
    clearErrors(){
        this.errors = [];
        this.shouldDisplayErrors = false;
        this.callbackOnError = undefined;
    }

    @action
    updateSelectedSchool(nextSchool: SimpleSchool, retainPath = false){
        let nextPath = `/${nextSchool.school_id}`;
        if(retainPath){
            const currentChosenSchool = (Utils.getChosenSchool() as string);
            const path = window.location.pathname;
            nextPath = path.replace(currentChosenSchool, nextSchool.school_id);
        }
        this.selectedSchool = nextSchool;
        Utils.setChosenSchoolInfo(nextSchool.school_id);
        featureGatingStore.fetchFeatureGatingList(nextSchool.school_id, true);
        window.location.href = nextPath;
    }

    @flow
    *startMasquerade(email: string){
        this.isChangingMasquerade = true;
        try{
            const resp = yield this.client.startMasquerade(email);
            this.handleMasqueradeAction(resp.data.data);
        } catch(e){
            this.addError(e);
        } finally {
            this.isChangingMasquerade = false;
        }
    }

    @flow
    *stopMasquerade(){
        this.isChangingMasquerade = true;
        try {
            const resp = yield this.client.stopMasquerade();
            localStorage.removeItem("isMasquerade");
            this.handleMasqueradeAction(resp.data.data);
        } catch(e) {
            this.addError(e);
        } finally {
            this.isChangingMasquerade = false;
        }
    }

    handleMasqueradeAction = (data) => {
        // Set schools to undefined in config so that setStorageValues will update it to
        //  the new school list in data
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        CONFIG.schools = undefined;
        localStorage.removeItem("school_list");
        Utils.setStorageValues(data, () => {
            location.replace("/");
        });
    };
}

export { AppStore };
